import { API, Auth } from 'aws-amplify'
import AWS from 'aws-sdk'
import { parse, visit, Kind, OperationTypeNode } from 'graphql'
import typesOfFieldsRaw from './GraphQLTypes.json'
import { FEATURE_FLAGS } from 'src/constants'
import { getRegion } from 'src/views/wonde/CommonHelpers/featureToggles'

const typesOfFields = typesOfFieldsRaw

let growthbookInstance
const originalAPIGraphQL = API.graphql.bind(API)
let initialFlagPromise
let hasLoggedApiVersion = false

// Fetch global feature flag from Dynamo.
async function fetchFlagsBeforeGrowthbookSet() {
  let credentials
  try {
    credentials = await Auth.currentCredentials()
  } catch (err) {
    console.log('Error getting credentials', err)
    return false
  }

  AWS.config.update({
    credentials,
    region: getRegion(),
  })

  const documentClient = new AWS.DynamoDB.DocumentClient()

  console.log('Fetching initial GLOBAL feature flag settings from DynamoDB (DocumentClient).')

  const featureKey = FEATURE_FLAGS.STANDALONE_API

  try {
    const globalResult = await documentClient
      .query({
        TableName: process.env.REACT_APP_APPLICATION_FEATURE_FLAG_GLOBAL,
        IndexName: 'byFeatureKey',
        KeyConditionExpression: 'featureKey = :featureKey',
        ExpressionAttributeValues: {
          ':featureKey': featureKey,
        },
        Limit: 1,
      })
      .promise()

    const globalItem = globalResult.Items?.[0] || null
    return globalItem?.featureValue === 'true'
  } catch (err) {
    console.log('Error querying global feature flag from DynamoDB', err)
    return false
  }
}

// Evaluate whether rewriting should be enabled
async function isRewritingEnabled() {
  try {
    let enabled

    if (!growthbookInstance || !growthbookInstance.ready) {
      if (!initialFlagPromise) {
        initialFlagPromise = fetchFlagsBeforeGrowthbookSet()
      }

      enabled = (await initialFlagPromise) ?? false
    } else {
      enabled = growthbookInstance.isOn(FEATURE_FLAGS.STANDALONE_API)
    }

    if (enabled && !hasLoggedApiVersion) {
      console.log(
        '[GraphQLInterceptor] Feature flag STANDALONE_API está ON. Usando API V2 para las llamadas GraphQL.',
      )
      hasLoggedApiVersion = true
    } else if (!enabled && !hasLoggedApiVersion) {
      console.log(
        '[GraphQLInterceptor] Feature flag STANDALONE_API está OFF. Usando API legacy (V1).',
      )
      hasLoggedApiVersion = true
    }
    return enabled
  } catch (error) {
    console.log('Error determining flag value. Treating it as disabled.', error)
    return false
  }
}

async function transformGQL(gqlDoc) {
  if (await isRewritingEnabled()) {
    let fieldStack = []

    return visit(gqlDoc, {
      Field(field) {
        let modifiedField
        const fieldName = field.name.value
        const currentType = fieldStack[fieldStack.length - 1]
        let fieldType
        if (currentType) {
          const currentFieldTypes = typesOfFields[currentType]

          if (currentFieldTypes) {
            const aliasFieldToV2 =
              field.alias === undefined && `${fieldName}V2` in currentFieldTypes

            fieldType = currentFieldTypes[fieldName]
            if (aliasFieldToV2) {
              console.log(
                `[GraphQLInterceptor] Reescribiendo campo ${currentType}.${fieldName} -> ${fieldName}V2 (API V2)`,
              )
              modifiedField = {
                ...field,
                name: { kind: Kind.NAME, value: `${fieldName}V2` },
                alias: { kind: Kind.NAME, value: fieldName },
              }
            }
          } else {
            console.warn('Undefined field types for:', currentType)
          }
        }

        if (field.selectionSet) {
          fieldStack.push(fieldType)
        }

        return modifiedField
      },

      enter(node) {
        if (node.kind === Kind.OPERATION_DEFINITION) {
          if (node.operation === 'query' || node.operation === OperationTypeNode.QUERY) {
            fieldStack = ['Query']
          } else if (
            node.operation === 'mutation' ||
            node.operation === OperationTypeNode.MUTATION
          ) {
            fieldStack = ['Mutation']
          } else {
            fieldStack = [node.operation]
          }
        }
      },

      leave(node) {
        if (node.kind === Kind.SELECTION_SET) {
          fieldStack.pop()
        }
      },
    })
  }

  return gqlDoc
}

// Monkey patch API.graphQL to apply transformer
export function monkeyPatchGraphqlCalls(growthbook) {
  growthbookInstance = growthbook

  API.graphql = (input, additionalHeaders) => {
    const query = typeof input.query === 'string' ? parse(input.query) : input.query

    const subscription = query.definitions.find(
      (def) =>
        def.kind === Kind.OPERATION_DEFINITION && def.operation === OperationTypeNode.SUBSCRIPTION,
    )

    if (subscription) {
      return originalAPIGraphQL(input, additionalHeaders)
    }

    return transformGQL(query).then((transformed) =>
      originalAPIGraphQL(
        {
          ...input,
          query: transformed,
        },
        additionalHeaders,
      ),
    )
  }
}
